import { Route } from '@angular/router';

import { SITE_URL, ORGANIZATION_JSON_LD_ID, ORGANIZATION_NAME } from '../site';
import { BlogPost } from './blog-post';
import { BLOG_POSTS } from './blog-posts.generated';
import { BlogIndexComponent } from './blog-index.component';
import { BlogPostComponent } from './blog-post.component';

/*
 * Routes for the /blog section, one flat route per post.
 *
 * Flat and unparameterized on purpose: the prerender builder discovers routes from the
 * router configuration (discoverRoutes defaults to true in angular.json's prerender
 * target) and skips anything with a parameter, so a single 'blog/:slug' route would
 * never be prerendered. Generating one route per post instead means every post is
 * statically rendered without touching routes.txt — the same approach GUIDE_PAGES uses.
 */

const BLOG_INDEX_TITLE = 'Dash Blog: Meeting Room Displays & Product News';
const BLOG_INDEX_DESCRIPTION =
  'Product news, setup tips and practical advice on running meeting room displays with ' +
  'Dash, from the team that builds it.';

function absoluteUrl(pathname: string): string {
  return SITE_URL + pathname;
}

/*
 * Article structured data for a post. Emitted by AppComponent into its own
 * <script type="application/ld+json"> alongside the site-wide Organization and
 * SoftwareApplication graph in index.html, and referencing that Organization by @id
 * rather than restating it.
 */
function articleJsonLd(post: BlogPost): object {
  const url = absoluteUrl('/blog/' + post.slug);
  return {
    '@context': 'https://schema.org',
    '@type': 'Article',
    headline: post.title,
    description: post.description,
    image: absoluteUrl(post.heroImage.src),
    datePublished: post.datePublished,
    dateModified: post.dateModified,
    author: {
      '@type': 'Organization',
      '@id': ORGANIZATION_JSON_LD_ID,
      name: ORGANIZATION_NAME
    },
    publisher: { '@id': ORGANIZATION_JSON_LD_ID },
    mainEntityOfPage: { '@type': 'WebPage', '@id': url }
  };
}

/*
 * BreadcrumbList structured data mirroring the post's visible breadcrumb trail
 * (Blog / post title), emitted as its own block next to the Article.
 */
function breadcrumbJsonLd(post: BlogPost): object {
  return {
    '@context': 'https://schema.org',
    '@type': 'BreadcrumbList',
    itemListElement: [
      { '@type': 'ListItem', position: 1, name: 'Blog', item: absoluteUrl('/blog') },
      { '@type': 'ListItem', position: 2, name: post.title, item: absoluteUrl('/blog/' + post.slug) }
    ]
  };
}

/*
 * FAQPage structured data for a post with frequently asked questions, emitted as its own
 * block next to the Article. The same questions and answers are on the page itself, in
 * the post's "Common questions" section, as Google requires of FAQ markup.
 */
function faqPageJsonLd(post: BlogPost): object {
  return {
    '@context': 'https://schema.org',
    '@type': 'FAQPage',
    mainEntity: post.faq.map(entry => ({
      '@type': 'Question',
      name: entry.question,
      acceptedAnswer: { '@type': 'Answer', text: entry.answerHtml }
    }))
  };
}

export const BLOG_ROUTES: Route[] = [
  {
    path: 'blog',
    component: BlogIndexComponent,
    data: {
      fullTitle: BLOG_INDEX_TITLE,
      description: BLOG_INDEX_DESCRIPTION
    }
  },
  ...BLOG_POSTS.map((post): Route => ({
    path: `blog/${post.slug}`,
    component: BlogPostComponent,
    data: {
      fullTitle: post.title,
      description: post.description,
      ogImage: absoluteUrl(post.heroImage.src),
      ogType: 'article',
      articlePublishedTime: post.datePublished,
      articleModifiedTime: post.dateModified,
      jsonLd: [
        articleJsonLd(post),
        breadcrumbJsonLd(post),
        ...(post.faq.length ? [faqPageJsonLd(post)] : [])
      ],
      post
    }
  }))
];
